import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
const VerificationTemplate = lazy(
  () => import("../../shared/app/VerificationTemplate")
);
import { DeleteTemplateModal } from "../../shared/app/VerificationTemplate/modals/DeleteTemplateModal";
import { CreateTemplateModal } from "../../shared/app/VerificationTemplate/modals/CreateTemplateModal";
import { Layout } from "providers/Layout";
import { useVerificationTemplateController } from "./controller";
import {
  Template,
  ApplicantSettingsErrors
} from "types/landlord/verificationTemplate";
import { LayoutHeader } from "../../shared/app/VerificationTemplate/components/LayoutHeader";
import { Loader } from "shared/components/Loader";
import { useQuery } from "@tanstack/react-query";
import templatesApi from "api/landlord/templates";
import { useAuthContext } from "providers/Auth";
import { Redirect } from "shared/components/Redirect";
import { ApplicantType, VerificationNames } from "shared/types";
import { FEATURES } from "types/landlord/partner";
import { toast } from "react-toastify";
import { useVerificationTemplatesController } from "../verification-templates/controller";
import { SideBar } from "../../shared/app/VerificationTemplate/components/SideBar";
import { LayoutFooter } from "../../shared/app/VerificationTemplate/components/LayoutFooter";
import { validateTemplate } from "../../shared/app/VerificationTemplate/validations";
import {
  onMountTemplatePreparations,
  onSaveCleanUp
} from "../../shared/app/VerificationTemplate/helpers";

export const VerificationTemplatesIdView = () => {
  const navigate = useNavigate();
  const [template, setTemplate] = useState<Template | null>(null);
  const [duplicateTemplate, setDuplicateTemplate] = useState<Template | null>(
    null
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [applicantSettingsErrors, setApplicantSettingsErrors] =
    useState<ApplicantSettingsErrors | null>(null);

  const { defaultTemplate, templates } = useVerificationTemplatesController();

  const {
    templateQuery,
    deleteTemplate,
    updateTemplate,
    createTemplate,
    verificationSchemas
  } = useVerificationTemplateController();

  useQuery({
    queryKey: ["DEFAULT_CRITERIA_SETTINGS"],
    queryFn: () =>
      templatesApi.getDefaultCriteria().then((defaultCriteria) => {
        if (!template?.screening_criteria)
          setTemplate((prevTemplate: Template | null) =>
            prevTemplate
              ? {
                  ...prevTemplate,
                  screening_criteria: defaultCriteria
                }
              : null
          );
      }),
    enabled: Boolean(template)
  });

  const toggleEditMode = () => {
    if (isEditing && editedName !== template?.name)
      setTemplate((prevTemplate: Template | null) =>
        prevTemplate
          ? {
              ...prevTemplate,
              name: editedName
            }
          : null
      );

    setIsEditing(!isEditing);
  };

  useEffect(() => {
    setEditedName(template?.name || "");
  }, [template?.name]);

  useEffect(() => {
    if (templateQuery.data?.verification_template && defaultTemplate) {
      const currentVerificationTemplate =
        templateQuery.data.verification_template;
      const verification_schemas =
        onMountTemplatePreparations.populateDefaultSchemas(
          defaultTemplate,
          currentVerificationTemplate
        );
      currentVerificationTemplate.verification_schemas = verification_schemas;

      currentVerificationTemplate.settings = {
        ...currentVerificationTemplate.settings,
        applicant: {
          ...currentVerificationTemplate.settings.applicant,
          enabled_screening_services: true
        },
        co_signer: {
          ...currentVerificationTemplate.settings.co_signer,
          enabled_screening_services:
            currentVerificationTemplate.settings.co_signer
              .enabled_screening_services ?? true
        },
        occupant: {
          ...currentVerificationTemplate.settings.occupant,
          enabled_screening_services:
            currentVerificationTemplate.settings.occupant
              ?.enabled_screening_services ?? true
        }
      };
      currentVerificationTemplate.rule_criteria =
        onMountTemplatePreparations.normalizeRuleCriteria(
          currentVerificationTemplate.rule_criteria
        );

      setTemplate(currentVerificationTemplate);
    }
  }, [defaultTemplate, templateQuery.data?.verification_template]);

  const { type, auth, permissions, partner } = useAuthContext();

  if (!type.isBoomScreen) return <Redirect href="/" />;
  if (type.isPropertyManager && !auth?.can_access_screening_templates)
    return (
      <Redirect href={permissions.canManageDiscounts ? "/discounts" : "/"} />
    );

  if (templateQuery.isLoading || !defaultTemplate)
    return (
      <Layout>
        <Loader />
      </Layout>
    );

  if (templateQuery.isError)
    return (
      <Layout>
        <h1 className="text-lg font-bold">Error occurred finding template</h1>
        <p className="text-base">{templateQuery.error.message}</p>
      </Layout>
    );

  if (!template || !templateQuery.data?.verification_template)
    return (
      <Layout>
        <h1 className="text-lg font-bold">Template not found</h1>
      </Layout>
    );

  const handleCancel = () => {
    navigate(`/verification-templates`);
  };

  const handleSaveClick = async (newTemplate: Template) => {
    const validationResult = validateTemplate(newTemplate, {
      setApplicantSettingsErrors,
      onError: (message, type) => {
        if (type === "warning") toast.warning(message);
        else toast.error(message);
      }
    });

    if (!validationResult.isValid) return;

    let updatedTemplate = onSaveCleanUp.additionalQuestions(newTemplate);

    updatedTemplate = onSaveCleanUp.identityVerificationSettings(
      updatedTemplate,
      editedName
    );

    updatedTemplate = onSaveCleanUp.onlyNewIncomePresent(updatedTemplate);

    updatedTemplate = onSaveCleanUp.filterOutHousingHistory(updatedTemplate);

    updatedTemplate = onSaveCleanUp.syncCreditReportMissingPricing(
      updatedTemplate,
      verificationSchemas || []
    );

    updatedTemplate = onSaveCleanUp.disableBoomFinanceIfFeatureAbsent(
      updatedTemplate,
      Boolean(partner?.features.includes(FEATURES.WIP_BOOM_FINANCE))
    );

    updatedTemplate =
      onSaveCleanUp.disableBoomFinanceForGuarantors(updatedTemplate);

    updatedTemplate = onSaveCleanUp.petPolicy(updatedTemplate);

    updateTemplate(updatedTemplate);

    // Clear modals
    setShowDeleteModal(false);
    setShowDuplicateModal(false);
  };

  return (
    <Layout sectionClassName="!p-0 !overflow-hidden">
      <div className="bg-background-900 h-full">
        <Suspense fallback={<Loader />}>
          <VerificationTemplate
            layoutProps={{
              header: <LayoutHeader />,
              sidebar: (
                <SideBar
                  handleDuplicate={() => {
                    setDuplicateTemplate({ ...template });
                    setShowDuplicateModal(true);
                  }}
                  isEditing={isEditing}
                  template={template}
                  editedName={editedName}
                  setEditedName={setEditedName}
                  toggleEditMode={toggleEditMode}
                  setShowDeleteModal={setShowDeleteModal}
                  showBackButton
                />
              ),
              footer: (
                <LayoutFooter
                  onCancel={handleCancel}
                  onSave={(newTemplate) => {
                    if (!newTemplate.name)
                      toast.error("Template name is required");
                    handleSaveClick(newTemplate);
                  }}
                />
              )
            }}
            verificationTemplate={template}
            savedTemplate={templateQuery.data.verification_template}
            verificationSchemas={verificationSchemas || []}
            templates={templates}
            onTemplateUpdate={setTemplate}
            onApplicantSettingsErrorsUpdate={setApplicantSettingsErrors}
            originalQualificationsDisclosureText={
              template.verification_schemas[ApplicantType.Applicant][
                VerificationNames.TermsAndConditions
              ]?.additional_text || ""
            }
            originalSubmissionDisclosureText={
              template.verification_schemas[ApplicantType.Applicant][
                VerificationNames.SubmissionDisclosure
              ]?.additional_text || ""
            }
            applicantSettingsErrors={applicantSettingsErrors}
          />
        </Suspense>
      </div>

      <CreateTemplateModal
        opened={showDuplicateModal}
        template={duplicateTemplate}
        verificationSchemas={verificationSchemas || []}
        handleCancel={() => setShowDuplicateModal(false)}
        handleSave={createTemplate}
        onTemplateUpdate={setDuplicateTemplate}
      />
      <DeleteTemplateModal
        opened={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        handleDelete={deleteTemplate}
      />
    </Layout>
  );
};
